import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { SortingState } from "@tanstack/react-table";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import {
  handleServiceQuotationStatus,
  fetchAllServiceQuotations,
  deleteServiceQuotation,
  exportServiceQuotationCSV,
} from "slices/quotation";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import StatusDropdown from "Common/Components/StatusDropdown/statusDropdown";
import { quotationStatusOptions } from "Common/Components/StatusDropdown/statusOptions";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import DescriptionIcon from "@mui/icons-material/Description";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UndoIcon from "@mui/icons-material/Undo";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import { Col, Row } from "react-bootstrap";
import ExportButton from "Common/Components/Buttons/ExportButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import StatusBadge from "Common/Components/StatusBadge/StatusBadge";
import { toast } from "react-toastify";
import { showConfirm } from "helpers/swalHelper";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import TruncatedTextWithTooltip from "Common/Components/TruncatedTextWithTooltip";
import { app_modules } from "helpers/apps_modules_list";
import { usePermission } from "hooks/usePermission";
import { QUOTATION_STAGE } from "Constants/StatusConstant";
import Chip from "components/Chips/Chip";
import { Tooltip } from "@mui/material";

const QuotationDraft: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  const tableState = usePreservedTableState({
    key: TableKeys.QUOTATION.SERVICE.DRAFT,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  // State management
  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  // const [search, setSearch] = useState("");
  // const [searchInput, setSearchInput] = useState("");
  // const [columnFilters, setColumnFilters] = useState<any>();
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [filterValue, setFilterValue] = useState<any[]>([]);
  const [tableInstance, setTableInstance] = useState<any>();
  const [data, setData] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);

  // Redux selectors
  const { loading, error } = useSelector((state: any) => state.Quotation);

  const { canView, canUpdate } = usePermission(
    app_modules?.quotation?.app_name,
    app_modules?.quotation?.modules?.service
  );

  // Sorting parameters
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  // Data fetching
  const handleFetchQuotationData = useCallback(async () => {
    try {
      const resData = await dispatch(
        fetchAllServiceQuotations({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search: search,
          filters: filterValue,
          type: QUOTATION_STAGE.DRAFT,
        })
      ).unwrap();
      setData(resData?.data || []);
      setTotalRecords(resData?.rowCount || 0);
    } catch (err: any) {
      console.error("Error fetching quotations:", err);
      toast.error(err?.message || "Failed to fetch quotations");
      setData([]);
      setTotalRecords(0);
    }
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    filterValue,
    search,
  ]);

  // ✅ Debounced search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  // Effects
  useEffect(() => {
    handleFetchQuotationData();
  }, [handleFetchQuotationData]);


  // Handlers
  const viewRoute = "/quotation/service/view";

  const handleView = (quotationNo: string) => {
    navigate(`${viewRoute}/${quotationNo}`);
  };

  const handleEdit = (quotationNo: string) => {
    navigate(`/quotation/service/edit/${quotationNo}`);
  };

  const handleFollowUp = (quotationNo: string) => {
    navigate(`/quotation/followups/service/${quotationNo}`);
  };

  const handleCreateSalesOrder = (quotationNo: string) => {
    navigate(`/sales_order/service/create-from-quotation/${quotationNo}`);
  };

  const handleDelete = async (id: string, enquiryNumber?: string) => {
    const isFromEnquiry = Boolean(enquiryNumber);
    const result = await showConfirm(
      isFromEnquiry
        ? "This quotation will be reverted and the enquiry status will be changed back to 'In Progress'. Do you want to continue?"
        : "This quotation will be permanently deleted. Do you want to continue?",
      isFromEnquiry ? "Revert Quotation" : "Delete Quotation",
      isFromEnquiry ? "Yes, revert" : "Yes, delete",
      "Cancel"
    );

    if (!result.isConfirmed) return;

    try {
      await dispatch(deleteServiceQuotation(id)).unwrap();
      toast.success(
        isFromEnquiry
          ? "Quotation reverted successfully"
          : "Quotation deleted successfully"
      );
      handleFetchQuotationData();
    } catch (err: any) {
      console.error("Error deleting quotation:", err);
      toast.error(err?.message || "Failed to delete quotation");
    }
  };

  const handleStatusChange = async (
    quotationId: string,
    newStatus: string,
    remark?: string
  ) => {
    try {
      await dispatch(
        handleServiceQuotationStatus({
          id: quotationId,
          quotation_status: newStatus,
          remark: remark,
        })
      ).unwrap();
      handleFetchQuotationData();
    } catch (err) {
      console.error("Error updating status:", err);
    }
  };

  // Columns definition
  const getColumns = () => {
    const commonColumns = [
      // {
      //   header: "Sr. No.",
      //   accessorKey: "sr_no",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   cell: ({ row }: { row: any }) => row.original.sr_no,
      // },
      {
        header: "Quotation No.",
        accessorKey: "quotation_number",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: API_ENDPOINT.QUOTATION.SERVICE.FILTER_DROPDOWN(
          "quotation_number",
          QUOTATION_STAGE.DRAFT
        ),
        cell: ({ row }: { row: any }) => row.original.quotation_number || "-",
      },
      // {
      //   header: "Service Number",
      //   accessorKey: "service_number",
      //   enableColumnFilter: true,
      //   enableSorting: true,
      //   filterType: "dropdown" as const,
      //   filterFetchUrl: API_ENDPOINT.QUOTATION.SERVICE.FILTER_DROPDOWN(
      //     "service_number",
      //     QUOTATION_STAGE.DRAFT
      //   ),
      //   cell: ({ row }: { row: any }) => row.original.service_number || "-",
      // },
      {
        header: "Quotation Date",
        accessorKey: "quotation_date",
        id: "quotation_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        filterFetchUrl: API_ENDPOINT.QUOTATION.SERVICE.FILTER_DROPDOWN(
          "quotation_date",
          QUOTATION_STAGE.DRAFT
        ),
        cell: ({ row }: { row: any }) => (
          <FormattedDate date={row.original.quotation_date} />
        ),
      },
      {
        header: "Enquiry Ref",
        accessorKey: "enquiry_number",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: API_ENDPOINT.QUOTATION.SERVICE.FILTER_DROPDOWN(
          "enquiry_number",
          QUOTATION_STAGE.DRAFT
        ),
        cell: ({ row }: { row: any }) => row.original.enquiry_number || "-",
      },
      {
        header: "Customer Name",
        accessorKey: "customer_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: API_ENDPOINT.QUOTATION.SERVICE.FILTER_DROPDOWN(
          "customer_name",
          QUOTATION_STAGE.DRAFT
        ),
        cell: ({ row }: { row: any }) =>
          row.original.customer_name || "-",
      },
      {
        header: "Organisation",
        id: "organisation_name",
        accessorFn: (row: any) => {
          const billFrom = row?.bill_from;
          if (!billFrom) return "-";
          let value = billFrom;
          if (typeof value === "string") {
            try {
              value = JSON.parse(value);
            } catch {
              return value.trim() || "-";
            }
          }
          if (typeof value !== "object" || Array.isArray(value)) return "-";
          return (
            value.organization_name ||
            value.organisation_name ||
            value.org_name ||
            value.name ||
            "-"
          );
        },
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ getValue }: { getValue: () => unknown }) =>
          (getValue() as string) || "-",
      },
      {
        header: "Agent",
        accessorKey: "agent_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: API_ENDPOINT.QUOTATION.SERVICE.FILTER_DROPDOWN(
          "agent_name",
          QUOTATION_STAGE.DRAFT
        ),
        cell: ({ row }: { row: any }) => row.original.agent_name || "-",
      },
      // {
      //   header: "Status",
      //   accessorKey: "quotation_status",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   cell: ({ row }: { row: any }) => {
      //     const status = row.original.quotation_status;
      //     return (
      //       <StatusBadge
      //         status={"Draft"}
      //         bgColor={status ? "#f7acf0ff" : "#fde4b4"}
      //         color="#000000ff"
      //       />
      //     );
      //   },
      // },
      {
        header: "Assigned To",
        accessorKey: "assigned_member_details",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <div
            style={{
              whiteSpace: "normal",
              wordWrap: "break-word",
              minWidth: "150px",
            }}
          >
            {row.original.assigned_members
              ?.map((member: any) => member.username)
              .join(", ") || "-"}
          </div>
        ),
      },
      {
        header: "Notes",
        accessorKey: "notes",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <TruncatedTextWithTooltip text={row.original.notes} />
        ),
      },
      {
        header: "Tags",
        accessorKey: "tags",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const tags: string[] = row.original.tags || [];
          if (!tags.length) return "-";

          const maxVisible = 3;
          const visible = tags.slice(0, maxVisible);
          const remaining = tags.length - visible.length;
          const tooltipText = tags.join(", ");

          return (
            <Tooltip
              arrow
              placement="top"
              title={tooltipText}
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    fontSize: "13px",
                    whiteSpace: "normal",
                  },
                },
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "4px",
                  minWidth: "150px",
                  cursor: "pointer",
                }}
              >
                {visible.map((tag: string) => (
                  <Chip key={tag}>{tag}</Chip>
                ))}
                {remaining > 0 && <Chip>+{remaining} more</Chip>}
              </div>
            </Tooltip>
          );
        },
      },
      {
        header: "Created By/ Date",
        accessorKey: "created_at",
        id: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <NameWithDate
            name={row.original.created_by_user?.username || "-"}
            date={row.original.created_at}
          />
        ),
      },
      {
        header: "Updated By/ Date",
        accessorKey: "updated_at",
        id: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <NameWithDate
            name={row.original.updated_by_user?.username || "-"}
            date={row.original.updated_at}
          />
        ),
      },
    ];

    const actionColumn = {
      header: "Action",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: { row: any }) => {
        const quotaionID = row.original.quotation_service_id;

        const actions = [
          {
            label: "View",
            permission: canView,
            icon: <VisibilityIcon fontSize="small" color="info" />,
            onClick: () => handleView(quotaionID),
          },
          {
            label: "Edit",
            permission: canUpdate,
            icon: <EditIcon fontSize="small" color="primary" />,
            onClick: () => handleEdit(quotaionID),
          },
          // {
          //   label: "Follow up",
          //   icon: <PersonAddIcon fontSize="small" color="success" />,
          //   onClick: () => handleFollowUp(quotaionID),
          // },
          // {
          //   label: "Create Sales Order",
          //   icon: <DescriptionIcon fontSize="small" color="warning" />,
          //   onClick: () => handleCreateSalesOrder(quotaionID),
          // },
          {
            label: row.original.enquiry_number ? "Revert" : "Delete",
            permission: canUpdate,
            icon: row.original.enquiry_number ? (
              <UndoIcon fontSize="small" color="warning" />
            ) : (
              <DeleteOutlineIcon fontSize="small" color="error" />
            ),
            onClick: () =>
              handleDelete(quotaionID, row.original.enquiry_number),
            className: "text-danger",
          },
        ];

        return <DynamicMenuItem row={row.original} menuItems={actions} />;
      },
    };
    return [...commonColumns, actionColumn];
  };

  return (
    <div className="quotation-listing-body">
      <div className="flex-shrink-0">
        <Row className="align-items-center quotation-search-row g-2">
          <SearchBox
            value={searchInput}
            onChange={(e: any) => setSearchInput(e.target.value)}
            placeholder="Search..."
          />

          <Col
            xs={12}
            md={12}
            lg={8}
            xl={7}
            className="d-flex justify-content-end align-items-center gap-2"
          >
            {tableInstance && (
              <ColumnVisibilityDropdown
                columns={tableInstance.getAllLeafColumns()}
                excludedColumns={["action"]}
              />
            )}
            <ExportButton
              onExport={async () => {
                return await exportServiceQuotationCSV({
                  search: search,
                  filters: filterValue,
                  type: QUOTATION_STAGE.DRAFT,
                });
              }}
            />
          </Col>
        </Row>
      </div>

      <div
        className="quotation-table-scroll"
        style={{
          flex: "1 1 0",
          maxHeight: "100%",
          minHeight: 0,
          marginTop: 4,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <LoaderErrorWrapper loading={loading}>
          <MasterTableContainer
            columns={getColumns()}
            data={data}
            totalRecords={totalRecords}
            pageIndex={pageIndex}
            pageSize={pageSize}
            onPageChange={setPageIndex}
            onPageSizeChange={setPageSize}
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
            colValue={setFilterValue}
            sorting={sorting}
            onSortingChange={setSorting}
            manualSorting={true}
            isBordered={false}
            isPagination={true}
            isGlobalFilter={false}
            SearchPlaceholder="Search service quotations..."
            onTableReady={setTableInstance}
          />
        </LoaderErrorWrapper>
      </div>
    </div>
  );
};

export default QuotationDraft;
